import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { SAGA_PAYLOAD_TYPE } from "../../models/types/SagaPayloadType";
import { analyticsServices } from "../../services/api-services/AnalyticsServices";
import { webToastService } from "../../services/WebToastService";
import {
  ADMIN_ANALYTICS_STATS_GET,
  ADMIN_SUMMARY_STATS_DOWNLOAD,
  SAFETY_CHECK_LINK,
} from "../actions/actions.constants";
import {
  adminGetAnalyticsStatsCompletedAction,
  adminGetAnalyticsStatsErrorAction,
  adminDownloadSummaryStatsCompletedAction,
  adminDownloadSummaryStatsErrorAction,
  safetyCheckLinkCompleteAction,
  safetyCheckLinkErrorAction,
} from "../actions/analytics.action";
import { LinkSafetyStatus } from "../../utils/enums";

function* analyticsStatsSaga(data: SAGA_PAYLOAD_TYPE): any {
  try {
    const response = yield call(analyticsServices.get);
    yield put(
      adminGetAnalyticsStatsCompletedAction({ res: { stats: response.stats } })
    );
  } catch (e: any) {
    yield put(
      adminGetAnalyticsStatsErrorAction({
        message: (e?.errors && e.errors[0]?.message) || e?.message,
      })
    );
  }
}

function* safetyCheckLinkSaga(data: SAGA_PAYLOAD_TYPE): any {
  const payload = data.payload;
  try {
    const response = yield call(
      analyticsServices.safetyCheckLink,
      payload?.req
    );
    yield put(
      safetyCheckLinkCompleteAction({
        res: {
          link: payload.req.link,
          status:
            response.status && response.status?.length > 0
              ? response.status.includes[LinkSafetyStatus.MALWARE]
                ? LinkSafetyStatus.MALWARE
                : response.status.includes[
                    (LinkSafetyStatus.SOCIAL_ENGINEERING,
                    LinkSafetyStatus.SOCIAL_ENGINEERING_EXTENDED_COVERAGE)
                  ]
                ? LinkSafetyStatus.SOCIAL_ENGINEERING
                : LinkSafetyStatus.UNWANTED_SOFTWARE
              : LinkSafetyStatus.SAFE,
        },
      })
    );
  } catch (e: any) {
    yield put(
      safetyCheckLinkErrorAction({
        message: (e?.errors && e.errors[0]?.message) || e?.message,
      })
    );
  }
}

function* downloadSummaryStatsSaga(data: SAGA_PAYLOAD_TYPE): any {
  try {
    const response = yield call(analyticsServices.downloadSummaryStats);
    const statsData = response.data || response;
    
    // Convert data to CSV format
    const csvRows: string[] = [];
    
    // Main stats section
    csvRows.push(`Generated At,${statsData.generated_at || ""}`);
    csvRows.push("Summary Statistics");
    csvRows.push("");
    csvRows.push("Metric,Value");
    csvRows.push(`Total Users,${statsData.total_users || 0}`);
    csvRows.push(`Marketing Opted In Users,${statsData.marketing_opted_in_users || 0}`);
    csvRows.push(`Quest Creation Last 24 Hours,${statsData.quest_creation_last_24_hours || 0}`);
    csvRows.push(""); // Empty row for spacing
    
    // Quest creation by game section
    if (statsData.quest_creation_by_game && Array.isArray(statsData.quest_creation_by_game) && statsData.quest_creation_by_game.length > 0) {
      csvRows.push("Quest Creation By Game");
      csvRows.push("Game Title,Quest Count");
      statsData.quest_creation_by_game.forEach((game: any) => {
        csvRows.push(`${game.game_title || ""},${game.quest_count || 0}`);
      });
    }
    
    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    
    // Generate filename: Summary-stats-{generated_at}
    let fileName = "Summary-stats";
    if (statsData.generated_at) {
      const generatedAtDate = new Date(statsData.generated_at);
      const dateTimeString = generatedAtDate.toISOString()
        .replace(/T/, '-')
        .replace(/\..+/, '')
        .replace(/:/g, '-');
      fileName = `Summary-stats-${dateTimeString}.csv`;
    } else {
      // Fallback to current date/time if generated_at is not available
      const now = new Date();
      const dateTimeString = now.toISOString()
        .replace(/T/, '-')
        .replace(/\..+/, '')
        .replace(/:/g, '-');
      fileName = `Summary-stats-${dateTimeString}.csv`;
    }
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    yield put(adminDownloadSummaryStatsCompletedAction());
    webToastService.showSuccess(
      "Will send data to your email when it is completed"
    );
  } catch (e: any) {
    yield put(
      adminDownloadSummaryStatsErrorAction({
        message: (e?.errors && e.errors[0]?.message) || e?.message,
      })
    );
    webToastService.showError(
      (e?.errors && e.errors[0]?.message) || e?.message || "Failed to download stats"
    );
  }
}

function* analyticsSaga() {
  yield all([
    takeLatest(ADMIN_ANALYTICS_STATS_GET, analyticsStatsSaga),
    takeEvery(SAFETY_CHECK_LINK, safetyCheckLinkSaga),
    takeLatest(ADMIN_SUMMARY_STATS_DOWNLOAD, downloadSummaryStatsSaga),
  ]);
}

export default analyticsSaga;
